import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DsAlert } from "../../design-system";
import { usePermissions } from "../../rbac/PermissionsContext";
import { ADMIN_SHELL_NAV_EVENT } from "../../utils/adminShellNav";
import AssetDashboardPanel from "./assets/AssetDashboardPanel";
import AssetImportExportPanel from "./assets/AssetImportExportPanel";
import AssetOperationsPanel from "./assets/AssetOperationsPanel";
import AssetRegisterPanel from "./assets/AssetRegisterPanel";
import AssetReportsPanel from "./assets/AssetReportsPanel";
import AssetRequestsPanel from "./assets/AssetRequestsPanel";
import AssetSettingsPanel from "./assets/AssetSettingsPanel";
import {
  ALL_ASSET_TABS,
  HUB_ASSET_TABS,
  PRIMARY_ASSET_TABS,
} from "./assets/constants";

const VALID_TABS = ALL_ASSET_TABS.map((t) => t.id);
const HUB_TAB_IDS = new Set(HUB_ASSET_TABS.map((t) => t.id));

function resolveTab(raw) {
  const tab = String(raw || "dashboard").toLowerCase();
  if (tab === "asset_list" || tab === "list") return "register";
  if (tab === "asset_dashboard") return "dashboard";
  if (tab === "asset_requests") return "requests";
  if (tab === "asset_operations") return "operations";
  if (tab === "asset_settings") return "settings";
  if (tab === "asset_import") return "import";
  if (tab === "asset_reports") return "reports";
  if (tab === "assign_asset") return "operations";
  return VALID_TABS.includes(tab) ? tab : "dashboard";
}

export default function Assets() {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    resolveTab(searchParams.get("tab"))
  );
  const [sub, setSub] = useState(() => searchParams.get("sub") || "");
  const [navFilters, setNavFilters] = useState({});

  const canViewModule =
    isAdmin ||
    hasModuleAccess("assets", "view") ||
    ALL_ASSET_TABS.some((t) => hasScreenAccess("assets", t.screen, "view"));

  const canViewTab = useCallback(
    (tabId) => {
      if (isAdmin) return true;
      const def = ALL_ASSET_TABS.find((t) => t.id === tabId);
      if (!def) return false;
      return (
        hasScreenAccess("assets", def.screen, "view") ||
        hasModuleAccess("assets", "view")
      );
    },
    [hasModuleAccess, hasScreenAccess, isAdmin]
  );

  const primaryTabs = useMemo(
    () => PRIMARY_ASSET_TABS.filter((t) => canViewTab(t.id)),
    [canViewTab]
  );

  const canCreate =
    isAdmin ||
    hasModuleAccess("assets", "create") ||
    hasScreenAccess("assets", "asset_list", "create");
  const canEdit =
    isAdmin ||
    hasModuleAccess("assets", "edit") ||
    hasScreenAccess("assets", "asset_list", "edit");
  const canAssign =
    isAdmin ||
    hasScreenAccess("assets", "assign_asset", "view") ||
    hasScreenAccess("assets", "assign_asset", "create") ||
    hasScreenAccess("assets", "asset_operations", "edit");
  const canManageRequests =
    isAdmin ||
    hasScreenAccess("assets", "asset_requests", "edit") ||
    hasScreenAccess("assets", "asset_requests", "approve") ||
    hasModuleAccess("assets", "edit");
  const canEditSettings =
    isAdmin ||
    hasModuleAccess("assets", "edit") ||
    hasScreenAccess("assets", "asset_settings", "edit");
  const canExport =
    isAdmin ||
    hasModuleAccess("assets", "export") ||
    hasScreenAccess("assets", "asset_reports", "export") ||
    hasScreenAccess("assets", "asset_import", "export") ||
    hasScreenAccess("assets", "asset_list", "export");

  const writeUrl = useCallback(
    (tabId, nextSub, replace = true) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tabId);
      if (nextSub) next.set("sub", nextSub);
      else next.delete("sub");
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams]
  );

  const applyTab = useCallback(
    (tabId, nextSub, filters = {}) => {
      const resolved = resolveTab(tabId);
      if (!canViewTab(resolved)) {
        const fallback =
          primaryTabs[0]?.id ||
          VALID_TABS.find((t) => canViewTab(t)) ||
          "dashboard";
        setActiveTab(fallback);
        setSub("");
        writeUrl(fallback, "");
        return;
      }
      const subToUse =
        nextSub != null ? nextSub : filters?.sub != null ? filters.sub : "";
      setActiveTab(resolved);
      setSub(subToUse);
      setNavFilters(filters || {});
      writeUrl(resolved, subToUse);
    },
    [canViewTab, primaryTabs, writeUrl]
  );

  useEffect(() => {
    const tab = resolveTab(searchParams.get("tab"));
    const nextSub = searchParams.get("sub") || "";
    setActiveTab(tab);
    setSub(nextSub);
  }, [searchParams]);

  useEffect(() => {
    const onShellNav = (event) => {
      if (event?.detail?.module !== "assets") return;
      applyTab(event.detail.tab, event.detail.sub, event.detail.filters || {});
    };
    window.addEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
    return () => window.removeEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
  }, [applyTab]);

  useEffect(() => {
    if (!canViewTab(activeTab)) {
      const fallback = primaryTabs[0]?.id || "dashboard";
      if (fallback !== activeTab) applyTab(fallback, "");
    }
  }, [activeTab, applyTab, canViewTab, primaryTabs]);

  const isHubTab = HUB_TAB_IDS.has(activeTab);
  const hubLabel = HUB_ASSET_TABS.find((t) => t.id === activeTab)?.label;

  if (!canViewModule) {
    return (
      <div className="ds-page">
        <div className="ds-page-shell flex items-center justify-center p-6">
          <div className="max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-6 text-center">
            <h4 className="ds-h4 mb-1">No assets access</h4>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              You do not have permission to view Assets. Contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ds-page">
      <div className="ds-page-shell">
        <div className="ds-page-body flex h-full min-h-0 w-full flex-1 overflow-hidden">
          <aside className="ds-page-aside">
            <div className="ds-page-aside-head">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Assets
              </h4>
            </div>
            <div className="ds-page-aside-nav">
              {primaryTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={
                    !isHubTab && activeTab === t.id
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"
                  }
                  onClick={() => applyTab(t.id, "")}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </aside>

          <div className="ds-page-main">
            <div className="ds-page-content flex min-h-0 flex-1 flex-col overflow-y-auto text-[hsl(var(--ds-fg))]">
              {isHubTab ? (
                <div className="ds-page-alert mb-3">
                  <DsAlert tone="info">
                    Opened from Admin {hubLabel} hub
                  </DsAlert>
                </div>
              ) : null}

              {activeTab === "dashboard" && canViewTab("dashboard") ? (
                <AssetDashboardPanel
                  onNavigate={(tab, filters) => applyTab(tab, filters?.sub || "", filters)}
                />
              ) : null}

              {activeTab === "register" && canViewTab("register") ? (
                <AssetRegisterPanel
                  canCreate={canCreate}
                  canEdit={canEdit}
                  canAssign={canAssign}
                  initialFilters={navFilters}
                />
              ) : null}

              {activeTab === "requests" && canViewTab("requests") ? (
                <AssetRequestsPanel
                  canManage={canManageRequests}
                  initialFilters={navFilters}
                />
              ) : null}

              {activeTab === "operations" && canViewTab("operations") ? (
                <AssetOperationsPanel
                  canAssign={canAssign}
                  sub={sub || navFilters.sub || "stock"}
                  onSubChange={(next) => applyTab("operations", next)}
                />
              ) : null}

              {activeTab === "settings" && canViewTab("settings") ? (
                <AssetSettingsPanel
                  canEdit={canEditSettings}
                  sub={sub || "categories"}
                  onSubChange={(next) => applyTab("settings", next)}
                />
              ) : null}

              {activeTab === "import" && canViewTab("import") ? (
                <AssetImportExportPanel mode="import" canExport={canExport} />
              ) : null}

              {activeTab === "export" && canViewTab("export") ? (
                <AssetImportExportPanel mode="export" canExport={canExport} />
              ) : null}

              {activeTab === "reports" && canViewTab("reports") ? (
                <AssetReportsPanel
                  canExport={canExport}
                  initialFilters={navFilters}
                  sub={sub}
                />
              ) : null}

              {!canViewTab(activeTab) ? (
                <DsAlert tone="warning">
                  You do not have access to this Assets screen.
                </DsAlert>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
